import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Ban,
  FileText,
  Layers,
  Loader2,
  Puzzle,
  Save,
  Shield,
  Trash2,
  UserCheck,
} from 'lucide-react';
import { API_URL } from '../../config';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import TypedConfirmDialog from '../../components/superadmin/TypedConfirmDialog';
import PlatformOrgSubscriptionEditor from './PlatformOrgSubscriptionEditor';
import BillingHistorySection from '../../components/billing/customer/BillingHistorySection';
import { fetchAdminAddons } from '../../lib/adminCatalogApi';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function defaultPeriodEnd() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

async function fetchUserDetail(userId) {
  try {
    const res = await axios.get(`${API_URL}/platform/users/detail/${userId}`);
    return res.data;
  } catch (e) {
    if (e.response?.status === 404) {
      const fallback = await axios.get(`${API_URL}/platform/users/${userId}`);
      return fallback.data;
    }
    throw e;
  }
}

export default function SuperAdminUserDetailPanel({ userId, onUpdated, onDeleted, embedded = false }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [banOpen, setBanOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [addons, setAddons] = useState([]);
  const [grantAddonId, setGrantAddonId] = useState('');
  const [grantQty, setGrantQty] = useState(1);
  const [grantUntil, setGrantUntil] = useState(defaultPeriodEnd());
  const [granting, setGranting] = useState(false);
  const [subscriptionKey, setSubscriptionKey] = useState(0);
  const [orgSubscription, setOrgSubscription] = useState(null);
  const [billingHistory, setBillingHistory] = useState({ payments: [], activity: [] });
  const [content, setContent] = useState({ spaces: [], documents: [] });
  const [contentLoading, setContentLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('account');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await fetchUserDetail(userId);
      setDetail(data);
      setName(data.name || '');
      setEmail(data.email || '');
      const orgs = data.organizations || [];
      setSelectedOrgId((prev) => {
        if (prev && orgs.some((o) => o.organization_id === prev)) return prev;
        return orgs[0]?.organization_id || null;
      });
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to load user — restart the backend if this persists');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadOrgSubscription = useCallback(async () => {
    if (!selectedOrgId) {
      setOrgSubscription(null);
      setBillingHistory({ payments: [], activity: [] });
      return;
    }
    try {
      const [orgRes, histRes] = await Promise.all([
        axios.get(`${API_URL}/platform/organizations/${selectedOrgId}`),
        axios.get(`${API_URL}/platform/organizations/${selectedOrgId}/billing-history`).catch(() => ({
          data: { payments: [], activity: [] },
        })),
      ]);
      setOrgSubscription(orgRes.data.subscription);
      setBillingHistory({
        payments: histRes.data?.payments || [],
        activity: histRes.data?.activity || [],
      });
    } catch {
      setOrgSubscription(null);
    }
  }, [selectedOrgId]);

  const loadContent = useCallback(async () => {
    if (!userId) return;
    setContentLoading(true);
    try {
      const res = await axios.get(`${API_URL}/platform/users/${userId}/content`);
      setContent(res.data || { spaces: [], documents: [] });
    } catch {
      setContent({ spaces: [], documents: [] });
    } finally {
      setContentLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setDetail(null);
    setActiveTab('account');
    load();
  }, [load]);

  useEffect(() => {
    loadOrgSubscription();
  }, [loadOrgSubscription, subscriptionKey]);

  useEffect(() => {
    if ((activeTab === 'docs' || activeTab === 'workspaces') && userId) loadContent();
  }, [activeTab, userId, loadContent]);

  useEffect(() => {
    fetchAdminAddons()
      .then((list) => setAddons(list || []))
      .catch(() => setAddons([]));
  }, []);

  const selectedOrg = useMemo(
    () => (detail?.organizations || []).find((o) => o.organization_id === selectedOrgId) || null,
    [detail, selectedOrgId]
  );

  const selectedAddon = addons.find((a) => a.id === grantAddonId);
  const catalogAddons = orgSubscription?.catalog_addons || [];

  const saveProfile = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/platform/users/${userId}`, {
        name: name.trim() || undefined,
        email: email.trim() || undefined,
      });
      setDetail(res.data);
      toast.success('User updated');
      onUpdated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const toggle2fa = async (enabled) => {
    if (!userId) return;
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/platform/users/${userId}/2fa`, {
        enabled: !!enabled,
        enforced: true,
      });
      toast.success(`2FA ${enabled ? 'enabled' : 'disabled'}`);
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBan = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/platform/users/${userId}/ban`, { reason: '' });
      setDetail(res.data);
      setBanOpen(false);
      toast.success('User banned');
      onUpdated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ban failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnban = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/platform/users/${userId}/unban`);
      setDetail(res.data);
      toast.success('User unbanned');
      onUpdated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unban failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/platform/users/${userId}`, {
        data: { confirmation: detail?.email },
      });
      toast.success('User deleted');
      setDeleteOpen(false);
      onDeleted?.(userId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setActionLoading(false);
    }
  };

  const grantAddon = async () => {
    if (!selectedOrgId || !grantAddonId || !grantUntil) return;
    setGranting(true);
    try {
      const periodEnd = new Date(grantUntil).toISOString();
      await axios.post(`${API_URL}/platform/organizations/${selectedOrgId}/addon-grant`, {
        addon_id: grantAddonId,
        period_end: periodEnd,
        quantity: selectedAddon?.is_quantity_based ? Number(grantQty) || 1 : 1,
      });
      toast.success('Add-on granted');
      setGrantUntil(defaultPeriodEnd());
      setSubscriptionKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grant failed');
    } finally {
      setGranting(false);
    }
  };

  if (!userId) {
    if (embedded) return null;
    return (
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="font-[Manrope] text-base">User details</CardTitle>
          <CardDescription>Select a user from the list to manage their account.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const loadingView = (
    <div className={`flex items-center justify-center gap-2 text-slate-500 ${embedded ? 'flex-1 py-16' : 'py-16'}`}>
      <Loader2 className="w-5 h-5 animate-spin" />
      Loading user…
    </div>
  );

  if (loading && !detail) {
    if (embedded) {
      return <div className="flex flex-col h-full min-h-0">{loadingView}</div>;
    }
    return (
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent>{loadingView}</CardContent>
      </Card>
    );
  }

  if (!detail) {
    const notFound = (
      <p className="text-sm text-slate-500 text-center py-16">
        User not found. Restart the backend if this persists.
      </p>
    );
    if (embedded) {
      return <div className="flex flex-col h-full min-h-0 items-center justify-center px-6">{notFound}</div>;
    }
    return (
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent>{notFound}</CardContent>
      </Card>
    );
  }

  const isSuperAdmin = detail.platform_role === 'super_admin';

  const headerBlock = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="font-[Manrope] text-lg font-semibold text-slate-900 dark:text-white truncate">
          {detail.email}
        </h2>
        <p className="font-mono text-xs text-slate-500 mt-0.5 truncate">{detail.id}</p>
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        {detail.banned ? <Badge variant="destructive">Banned</Badge> : null}
        {detail.deletion_requested_at ? (
          <Badge className="bg-amber-600">Deletion scheduled</Badge>
        ) : null}
        {isSuperAdmin ? (
          <Badge className="gap-1 bg-violet-600">
            <Shield className="w-3 h-3" />
            super_admin
          </Badge>
        ) : null}
      </div>
    </div>
  );

  const tabTriggers = (
    <>
      <TabsTrigger value="account" className="gap-1.5">
        <Shield className="w-3.5 h-3.5" />
        Account & plan
      </TabsTrigger>
      <TabsTrigger value="addons" className="gap-1.5">
        <Puzzle className="w-3.5 h-3.5" />
        Add-ons
      </TabsTrigger>
      <TabsTrigger value="docs" className="gap-1.5">
        <FileText className="w-3.5 h-3.5" />
        Documentation
      </TabsTrigger>
      <TabsTrigger value="workspaces" className="gap-1.5">
        <Layers className="w-3.5 h-3.5" />
        Workspaces
      </TabsTrigger>
    </>
  );

  const tabContents = (
    <>
          <TabsContent value="account" className="space-y-6 mt-0">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="admin-user-name">Name</Label>
                <Input
                  id="admin-user-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSuperAdmin}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-user-email">Email</Label>
                <Input
                  id="admin-user-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSuperAdmin}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
              <span>Created {formatDate(detail.created_at)}</span>
              <span>Email {detail.email_verified_at ? 'verified' : 'not verified'}</span>
              {detail.banned_at ? <span>Banned {formatDate(detail.banned_at)}</span> : null}
            </div>

            {(detail.organizations || []).length > 0 ? (
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                <Label htmlFor="admin-user-org">Workspace for billing</Label>
                <select
                  id="admin-user-org"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={selectedOrgId || ''}
                  onChange={(e) => setSelectedOrgId(e.target.value || null)}
                >
                  {(detail.organizations || []).map((o) => (
                    <option key={o.organization_id} value={o.organization_id}>
                      {o.organization_name} · {o.plan_name || o.plan_id} ({o.role})
                    </option>
                  ))}
                </select>
                {selectedOrg ? (
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    Active plan:{' '}
                    <strong className="text-slate-900 dark:text-white">
                      {selectedOrg.plan_name || selectedOrg.plan_id}
                    </strong>
                    {selectedOrg.subscription_status ? ` · ${selectedOrg.subscription_status}` : ''}
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No workspaces — user cannot have a plan yet.</p>
            )}

            {selectedOrgId ? (
              <PlatformOrgSubscriptionEditor
                key={`${selectedOrgId}-${subscriptionKey}`}
                organizationId={selectedOrgId}
                onSaved={() => {
                  load();
                  setSubscriptionKey((k) => k + 1);
                }}
                showBillingHistory={false}
              />
            ) : null}

            <BillingHistorySection
              payments={billingHistory.payments}
              activity={billingHistory.activity}
              compact
            />

            <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <div>
                <Label>Two-factor authentication</Label>
                <p className="text-xs text-slate-500 mt-0.5">Enforced for this account</p>
              </div>
              <Switch
                checked={!!(detail.two_fa_enforced ?? detail.two_fa_enabled)}
                onCheckedChange={toggle2fa}
                disabled={actionLoading || isSuperAdmin}
              />
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
              <Button onClick={saveProfile} disabled={saving || isSuperAdmin}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save profile
              </Button>
              {!isSuperAdmin ? (
                detail.banned ? (
                  <Button variant="outline" onClick={handleUnban} disabled={actionLoading}>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Unban
                  </Button>
                ) : (
                  <Button variant="outline" className="text-amber-700" onClick={() => setBanOpen(true)}>
                    <Ban className="w-4 h-4 mr-2" />
                    Ban user
                  </Button>
                )
              ) : null}
              {!isSuperAdmin ? (
                <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete user
                </Button>
              ) : null}
            </div>
          </TabsContent>

          <TabsContent value="addons" className="space-y-6 mt-0">
            {!selectedOrgId ? (
              <p className="text-sm text-slate-500">User has no workspace — add-ons require an organization.</p>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="addons-org">Workspace</Label>
                  <select
                    id="addons-org"
                    className="flex h-9 w-full max-w-md rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    value={selectedOrgId || ''}
                    onChange={(e) => setSelectedOrgId(e.target.value || null)}
                  >
                    {(detail.organizations || []).map((o) => (
                      <option key={o.organization_id} value={o.organization_id}>
                        {o.organization_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <h3 className="text-sm font-medium mb-2">Active add-ons</h3>
                  {catalogAddons.length === 0 ? (
                    <p className="text-sm text-slate-500">No active add-ons on this workspace.</p>
                  ) : (
                    <ul className="space-y-2">
                      {catalogAddons.map((a) => (
                        <li
                          key={a.addon_id}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm"
                        >
                          <span className="font-medium">{a.addon_id}</span>
                          <div className="flex flex-wrap gap-2">
                            {a.active ? (
                              <Badge className="bg-emerald-600">Active</Badge>
                            ) : (
                              <Badge variant="secondary">Inactive</Badge>
                            )}
                            {a.included_with_plan ? (
                              <Badge variant="outline">Included with plan</Badge>
                            ) : null}
                            {a.period_end ? (
                              <span className="text-xs text-slate-500">
                                until {formatDate(a.period_end)}
                              </span>
                            ) : null}
                            {a.purchased_quantity > 0 ? (
                              <span className="text-xs text-slate-500">×{a.purchased_quantity}</span>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-4">
                  <div>
                    <h3 className="text-sm font-medium">Grant add-on (timed)</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Activate until a date without payment.
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="grant-addon">Add-on</Label>
                      <select
                        id="grant-addon"
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                        value={grantAddonId}
                        onChange={(e) => setGrantAddonId(e.target.value)}
                      >
                        <option value="">Select…</option>
                        {addons.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="grant-until">Active until</Label>
                      <Input
                        id="grant-until"
                        type="datetime-local"
                        value={grantUntil}
                        onChange={(e) => setGrantUntil(e.target.value)}
                      />
                    </div>
                  </div>
                  {selectedAddon?.is_quantity_based ? (
                    <div className="space-y-2 max-w-xs">
                      <Label htmlFor="grant-qty">Quantity</Label>
                      <Input
                        id="grant-qty"
                        type="number"
                        min={1}
                        max={selectedAddon.max_quantity || 500}
                        value={grantQty}
                        onChange={(e) => setGrantQty(e.target.value)}
                      />
                    </div>
                  ) : null}
                  <Button onClick={grantAddon} disabled={granting || !grantAddonId || !grantUntil}>
                    {granting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Grant add-on
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="docs" className="mt-0">
            {contentLoading ? (
              <div className="flex items-center gap-2 text-slate-500 py-10">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading documentation…
              </div>
            ) : content.documents.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">No documents created by this user.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Space</TableHead>
                    <TableHead>Workspace</TableHead>
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {content.documents.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-medium">{d.title}</TableCell>
                      <TableCell className="text-sm text-slate-600">{d.space_name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{d.organization_name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(d.updated_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          <TabsContent value="workspaces" className="mt-0">
            {(detail.organizations || []).length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">No workspaces.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workspace</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Docs</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(detail.organizations || []).map((o) => {
                    const spaceCount = content.spaces.filter(
                      (s) => s.organization_id === o.organization_id
                    ).length;
                    const docCount = content.documents.filter(
                      (d) => d.organization_id === o.organization_id
                    ).length;
                    return (
                      <TableRow key={o.organization_id}>
                        <TableCell>
                          <div className="font-medium">{o.organization_name}</div>
                          <div className="text-xs text-slate-400 font-mono">{o.slug}</div>
                        </TableCell>
                        <TableCell>{o.role}</TableCell>
                        <TableCell>{o.plan_name || o.plan_id}</TableCell>
                        <TableCell>{o.subscription_status || '—'}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {activeTab === 'workspaces' && content.spaces.length === 0 && !contentLoading
                            ? '—'
                            : `${docCount} docs · ${spaceCount} spaces`}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {activeTab === 'workspaces' && !contentLoading && content.spaces.length > 0 ? (
              <div className="mt-6">
                <h3 className="text-sm font-medium mb-2">Spaces</h3>
                <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded-lg border border-slate-200 dark:border-slate-800">
                  {content.spaces.map((s) => (
                    <li key={s.id} className="px-4 py-2 text-sm flex justify-between gap-2">
                      <span>
                        <strong>{s.name}</strong>
                        <span className="text-slate-500 ml-2">{s.organization_name}</span>
                      </span>
                      <span className="text-slate-500">{s.document_count} docs</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </TabsContent>
    </>
  );

  const dialogs = (
    <>
      <TypedConfirmDialog
        open={banOpen}
        onOpenChange={setBanOpen}
        title="Ban this user?"
        description="They will not be able to sign in until you unban them."
        confirmPhrase={detail.email}
        confirmLabel="Ban user"
        loading={actionLoading}
        onConfirm={handleBan}
      />
      <TypedConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this user permanently?"
        description="This permanently deletes the account and every workspace where they are the only company admin (spaces, documents, subscriptions). Shared workspaces keep content but remove their membership. This cannot be undone."
        confirmPhrase={detail.email}
        confirmLabel="Delete user forever"
        loading={actionLoading}
        onConfirm={handleDelete}
      />
    </>
  );

  if (embedded) {
    return (
      <div className="flex flex-col h-full min-h-0 overflow-hidden bg-white dark:bg-slate-900">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex flex-col flex-1 min-h-0 overflow-hidden"
        >
          <div className="shrink-0 border-b border-slate-200 dark:border-slate-800 px-4 pt-4 pb-3">
            {headerBlock}
            <TabsList className="mt-3 flex flex-wrap h-auto gap-1 bg-slate-100/80 dark:bg-slate-800/50">
              {tabTriggers}
            </TabsList>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4">
            {tabContents}
          </div>
        </Tabs>
        {dialogs}
      </div>
    );
  }

  return (
    <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
      <CardHeader className="pb-3">{headerBlock}</CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 flex flex-wrap h-auto gap-1">{tabTriggers}</TabsList>
          {tabContents}
        </Tabs>
        {dialogs}
      </CardContent>
    </Card>
  );
}
